import React from 'react';
import { motion } from 'framer-motion';
import { PresentationIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
interface Channel {
  label: string;
  logo?: string;
  icon?: typeof PresentationIcon;
}
const LEFT_CHANNELS: readonly Channel[] = [{
  label: 'Email / CRM',
  logo: 'https://cdn.magicpatterns.com/uploads/eaVbSy8gkaLMxVXZn426xD/image.png'
}, {
  label: 'WhatsApp',
  logo: 'https://cdn.magicpatterns.com/uploads/ff2xrTo8njegc9HQfBiHHs/image.jpg'
}, {
  label: 'Website',
  logo: 'https://cdn.magicpatterns.com/uploads/p8hxGk8PzPNEJJ1JuXxQRJ/image.png'
}, {
  label: 'Social',
  logo: 'https://cdn.magicpatterns.com/uploads/i3HXhvnPCcq7MnMVtgzoGz/image.png'
}];
const RIGHT_CHANNELS: readonly Channel[] = [{
  label: 'QR code',
  logo: 'https://cdn.magicpatterns.com/uploads/5EBPJkAW8wtsVjG6Pdmtzv/image.png'
}, {
  label: 'Congress',
  logo: 'https://cdn.magicpatterns.com/uploads/gFpiinUzyP3uSTeNYjp6JX/image.png'
}, {
  label: 'Rep iPad / IVA',
  icon: PresentationIcon
}, {
  label: 'Paid media',
  logo: 'https://cdn.magicpatterns.com/uploads/x8cuoAZY8NxEd27vHPMXd5/image.png'
}];
function ChannelTile({
  channel,
  align,
  delay
}: {
  channel: Channel;
  align: 'left' | 'right';
  delay: number;
}) {
  return <motion.li initial={{
    opacity: 0,
    x: align === 'left' ? -20 : 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.45
  }} className={`flex min-h-0 flex-1 items-center gap-[clamp(0.8rem,1.5vw,1.4rem)] rounded-2xl border border-[var(--fg)]/10 bg-white px-[clamp(1rem,1.8vw,1.75rem)] shadow-sm ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-261" data-mp="h9twywppcqx9">
      {channel.icon ? <span className="flex h-[clamp(2.8rem,4vw,4.25rem)] w-[clamp(2.8rem,4vw,4.25rem)] flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--brand)]/10" data-id="element-262" data-mp="2s367lhx8iggy">
          <channel.icon className="h-[clamp(1.35rem,2vw,2.1rem)] w-[clamp(1.35rem,2vw,2.1rem)] text-[var(--brand)]" aria-hidden="true" data-id="element-263" data-mp="3qeqwexg39dan" />
        </span> : <span className="flex h-[clamp(2.8rem,4vw,4.25rem)] w-[clamp(2.8rem,4vw,4.25rem)] flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--surface)] p-2" data-id="element-264" data-mp="38zz4nq5qeef">
          <img src={channel.logo} alt="" className="h-full w-full object-contain" data-id="element-265" data-mp="i4fkm4co91z7" />
        </span>}
      <span className={`min-w-0 flex-1 text-[clamp(1.15rem,1.9vw,1.8rem)] font-extrabold leading-tight tracking-[-0.03em] text-[var(--fg)] ${align === 'right' ? 'text-right' : ''}`} data-id="element-266" data-mp="qy10xs4hiraq">
        {channel.label}
      </span>
    </motion.li>;
}
export function SlideChannelEntryPoints({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-268" data-mp="1fl0dnekrubwa">Meet HCPs wherever attention already exists.</SlideBanner>} data-id="element-267" data-mp="vwc8txb7yrqb">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-269" data-mp="1kjbmpn38sidg">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-270" data-mp="1akm5lwj671y9">
          Distribution
        </p>
        <h2 className="max-w-4xl text-[clamp(1.8rem,3.4vw,3.1rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-271" data-mp="ws65p4lkfk1k">
          One experience.{' '}
          <span className="text-[var(--brand)]" data-id="element-272" data-mp="mwclr05ioo9r">Many entry points.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.75rem,1.6vw,1.75rem)]" data-id="element-273" data-mp="rvfogdy0yiek">
        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-274" data-mp="dvep52xerj17">
          {LEFT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="left" delay={0.25 + index * 0.08} data-id="element-275" data-mp="atjpbzqpgnrj" data-mpi={index} />)}
        </ul>

        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="relative flex min-h-0 w-[20%] flex-shrink-0 flex-col items-center justify-center" data-id="element-276" data-mp="12p2at5mxk51">
          <PdfImage crop={boehringerImages.coverPhoto} alt="The Boehringer T2D Snak, shared across every channel" className="min-h-0 max-h-full w-auto max-w-full rounded-[1.25rem] object-contain drop-shadow-[0_22px_50px_rgba(23,32,51,0.2)]" data-id="element-277" data-mp="3j8tmqujnh4sz" />
          <span className="mt-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1.1vw,1rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-278" data-mp="39d0cxixbfpew">
            One Snak
          </span>
        </motion.div>

        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-279" data-mp="1k1vbx6rfjp6m">
          {RIGHT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="right" delay={0.25 + index * 0.08} data-id="element-280" data-mp="1otwlum14l3th" data-mpi={index} />)}
        </ul>
      </div>
    </InteriorSlide>;
}